/**
 * <g-notes> - a fast Google-Keep-style note board.
 *
 * Renders a masonry grid of <g-note-card> tiles (pinned first) plus an inline
 * composer. Notes are stored as MARKDOWN; the body is rendered with the tiny
 * escape-first renderer in ../util/markdown.js (no Milkdown). The component
 * owns create / edit / pin / colour / delete UI and emits events; the host
 * persists and re-pushes `.notes`.
 *
 * Clicking a tile opens the edit modal — the tile IS the affordance; there is no
 * pen icon. The modal is a <dialog class="modal"> held as a sibling of the grid,
 * so `_renderGrid`'s `innerHTML = ''` cannot reach it: a host re-push while you
 * are typing rebuilds the tiles underneath and leaves your keystrokes alone.
 * (It used to be an in-place card template, which that same re-push silently
 * destroyed.) `showModal()` promotes it to the top layer, so it escapes the
 * masonry `columns` container from inside the component's own subtree — which
 * in turn keeps every delegated listener below working with no extra wiring.
 *
 * Data goes in via JS properties (the golden rule): set `.notes` (and the
 * optional `.config`) — never through attributes.
 *
 * Drag-reorder uses SortableJS (loaded globally, like kanban/task-list). The
 * whole tile is the drag target — there is no handle, and the tile is
 * `select-none` so a press can only ever mean "drag", never "select a range".
 * Reordering stays WITHIN a group (pinned among pinned, others among others).
 * On drop it emits `note-moved` with the full new order; the component also
 * adopts that order itself and holds it until the host acknowledges, so an
 * unrelated re-push cannot undo the drag (see util/order.js). Degrades
 * gracefully (no drag) when SortableJS is absent or in readonly mode.
 *
 * @attr {string} empty-text - shown when there are no notes (default 'No notes yet')
 * @attr {boolean} loading   - presence = skeleton tiles
 * @attr {boolean} readonly  - presence = hide composer + per-card controls + drag + edit modal
 * @attr {string} empty-icon - Font Awesome classes for the empty state (default 'fa-regular fa-note-sticky')
 *
 * @fires note-created - { note }                       (composer add; note has no id)
 * @fires note-updated - { noteId, changes, note }      (title/body or { color })
 * @fires note-deleted - { noteId, note }
 * @fires note-pinned  - { noteId, pinned, note }
 * @fires note-moved   - { noteId, oldIndex, newIndex, notes }  (drag-reorder drop)
 * @fires note-clicked - { noteId, note } — CANCELABLE. Fires on any tile click
 *   that is not a control, a link, or card chrome. `preventDefault()` suppresses
 *   the built-in edit modal, for a host that opens its own detail view instead.
 */
import { renderMarkdown } from '../util/markdown.js';
import { htmlToMarkdown } from '../util/html-to-markdown.js';
import { NOTE_PALETTE, noteSwatchStyle, noteTintStyle, formatToolbar } from './g-note-card.js';
import { reconcileOrder } from '../util/order.js';
import { ensureDragStyles, DRAG_CLASSES } from '../util/drag.js';

// How long after a drop a click is still assumed to belong to that drop.
// SortableJS has no handle and no delay here, so a press-and-wiggle on a tile
// ends in a real click event. A boolean flag would be the obvious fix and the
// wrong one: a drop does not always produce a trailing click (touch, and drops
// across sections), and the stranded flag would then eat the user's next
// genuine click. A timestamp cannot get stuck.
const DRAG_CLICK_GRACE_MS = 300;

// Same idea for the modal's own dismissal: click #2 of a double-click lands on
// the backdrop, because by then the document is inert and the modal covers the
// viewport. Without this the tile would open and instantly close.
const MODAL_DISMISS_GRACE_MS = 300;

// The tile is the edit affordance, so everything that is NOT "open the editor"
// has to opt out. Controls and links speak for themselves; the card's chrome
// rows (toolbar, swatches, delete-confirm) carry a bare `data-noopen`.
//
// `data-noopen` and not `data-role`: _onClick's first line is
// `closest('[data-role]')`, so a role on a CONTAINER would resolve as the role
// for clicks on its inert children — a trap for every future branch.
const CARD_NO_OPEN = 'a, button, input, select, textarea, label, [contenteditable="true"], [data-noopen]';

export class GNotes extends HTMLElement {
  constructor() {
    super();
    this._notes = [];
    this._config = null;
    this._renderScheduled = false;
    this._editingId = null;
    this._colorPickerFor = null;
    this._confirmDeleteId = null;
    this._composerColor = '';
    this._isDragging = false;
    // The edit modal: built once, lives outside _gridWrap, never rebuilt by a render.
    this._dialog = null;
    this._editingNote = null;
    // The colour being tried on in the modal. Pending, not committed: Save writes
    // it alongside title/body, Cancel throws it away with them.
    this._editingColor = 'default';
    // A drop ends in a click. _isDragging is already false by then (onEnd clears
    // it first), so the drag has to leave a timestamp behind to be distinguishable.
    this._dragEndedAt = 0;
    this._modalOpenedAt = 0;
    this._pendingRender = false;
    this._sortables = [];
    // A drag the host has not acknowledged yet — see util/order.js.
    this._pendingOrder = null;
  }

  static get observedAttributes() { return ['empty-text', 'loading', 'readonly', 'empty-icon']; }

  get emptyIcon() {
    const v = this.getAttribute('empty-icon');
    return v && /^[\w\s-]+$/.test(v) ? v : 'fa-regular fa-note-sticky';
  }
  attributeChangedCallback(name) {
    // Going readonly mid-edit would leave an editable modal over a display-only
    // board — reachable from the demo's "Toggle readonly" button.
    if (name === 'readonly') { this._syncComposerVisibility(); if (this.readonly) this._closeEditModal(); }
    this.scheduleRender();
  }

  connectedCallback() {
    ensureDragStyles();
    this.classList.add('block');
    if (!this._gridWrap) {
      this._composerWrap = document.createElement('div');
      this._gridWrap = document.createElement('div');
      this.appendChild(this._composerWrap);
      this.appendChild(this._gridWrap);

      // A third child, NOT a body portal. showModal() promotes for *rendering*
      // only — the DOM tree and therefore the event path are untouched, so the
      // delegated click/mousedown listeners below still serve the modal's own
      // buttons. A body-level dialog would leave the format toolbar dead, and
      // would be shared mutable state across every <g-notes> on the page.
      this._dialog = document.createElement('dialog');
      this._dialog.className = 'modal';
      this._dialog.setAttribute('data-role', 'note-edit-modal');
      this._dialog.setAttribute('aria-label', 'Edit note');
      this.appendChild(this._dialog);

      // Bound in here, inside the once-only guard: unlike the host listeners
      // below, these are never removed, so binding them per-connect would stack.
      this._dialog.addEventListener('close', () => {
        this._editingId = null;
        this._editingNote = null;
        this._editingColor = 'default';
        // Drop the stale editor so no orphan [data-role="note-edit-body"] can be
        // picked up by _onMouseDown's scope query or a late _saveEdit.
        this._dialog.innerHTML = '';
      });
      // The backdrop is a <form method="dialog">; its submit IS the close. A
      // double-click on a tile lands click #2 on that backdrop — the document is
      // already inert and the modal covers the viewport — which would open and
      // instantly close. Swallow a submit that arrives too soon after opening.
      this._dialog.addEventListener('submit', (e) => {
        if (Date.now() - this._modalOpenedAt < MODAL_DISMISS_GRACE_MS) e.preventDefault();
      });
    }
    this._ensureComposer();
    this.scheduleRender();
    this.addEventListener('click', this._onClick);
    this.addEventListener('focusin', this._onFocusIn);
    this.addEventListener('mousedown', this._onMouseDown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('focusin', this._onFocusIn);
    this.removeEventListener('mousedown', this._onMouseDown);
    if (this._dialog && this._dialog.open) this._dialog.close();
    this._destroySortable();
  }

  // ── Public API ──

  get notes() { return this._notes; }
  set notes(v) {
    const next = Array.isArray(v) ? v : [];
    // Hold a just-performed drag if this push predates it — see util/order.js.
    const { list, pending } = reconcileOrder(next, this._pendingOrder, (n) => this._noteId(n));
    this._notes = list;
    this._pendingOrder = pending;
    this.scheduleRender();
  }
  get data() { return this._notes; }
  set data(v) { this.notes = v; }

  get config() { return this._config; }
  set config(c) { this._config = c && typeof c === 'object' ? c : null; this._ensureComposer(true); this.scheduleRender(); }

  get emptyText() { return this.getAttribute('empty-text') || 'No notes yet'; }
  get loading() { return this.hasAttribute('loading'); }
  get readonly() { return this.hasAttribute('readonly'); }

  // ── Field mapping ──

  _fmap() { return (this._config && this._config.fields) || {}; }
  _palette() {
    const p = this._config && this._config.palette;
    return Array.isArray(p) && p.length ? p : NOTE_PALETTE;
  }
  _noteId(n) { const k = this._fmap().id || 'id'; let id = n && n[k]; if (id == null && n) id = n._id; return id == null ? '' : String(id); }
  _title(n) { return n[this._fmap().title || 'title']; }
  _body(n) { return n[this._fmap().body || 'body']; }
  _color(n) { return n[this._fmap().color || 'color']; }
  _pinned(n) { return !!n[this._fmap().pinned || 'pinned']; }
  _updatedAt(n) { return n[this._fmap().updatedAt || 'updatedAt']; }

  // ── Composer (built once, never rebuilt, to preserve typing) ──

  // A contenteditable has no placeholder attribute, so the empty composer would
  // otherwise be a blank box with no hint once the trigger row is hidden.
  _ensureComposerStyles() {
    if (typeof document === 'undefined' || document.getElementById('g-notes-composer-styles')) return;
    const style = document.createElement('style');
    style.id = 'g-notes-composer-styles';
    style.textContent = `[data-role="composer-body"]:empty::before{content:attr(data-placeholder);color:var(--color-base-content);opacity:.45;pointer-events:none}`;
    document.head.appendChild(style);
  }

  _ensureComposer(rebuild = false) {
    if (!this._composerWrap) return;
    this._ensureComposerStyles();
    if (this.readonly) { this._composerWrap.hidden = true; this._composerWrap.innerHTML = ''; this._composerBuilt = false; return; }
    this._composerWrap.hidden = false;
    if (this._composerBuilt && !rebuild) return;
    // Centred and capped, Keep-style. Collapsed and expanded are the same box —
    // the width is on the wrapper, so the placeholder row and the open form line
    // up exactly instead of the trigger spanning the whole board.
    this._composerWrap.className = 'mb-4 w-full max-w-xl mx-auto';
    const swatches = this._palette().map(key => `
      <button data-role="composer-color" data-color="${escapeHtml(key)}" title="${escapeHtml(key)}"
        class="w-5 h-5 rounded-full border border-base-content/20${key === 'default' ? ' bg-base-100' : ''}"
        style="${noteSwatchStyle(key)}"></button>`).join('');
    // Same chrome-free treatment as the edit modal: transparent, borderless
    // fields sharing one left edge, so the colour you pick washes the whole box
    // instead of outlining a white form. The body still GROWS here (min-h-24 /
    // max-h-72) rather than taking the modal's fixed height — the composer opens
    // small and earns its space; the modal opens at full size already.
    this._composerWrap.innerHTML = `
      <div data-role="composer-box" class="rounded-lg border border-base-300 bg-base-100 shadow-sm p-2">
        <input data-role="composer-trigger"
          class="w-full bg-transparent border-0 px-1 py-1 text-sm leading-snug placeholder:opacity-50 focus:outline-none"
          placeholder="Take a note…">
        <div data-role="composer-expanded" class="hidden flex flex-col gap-2">
          <input data-role="composer-title"
            class="w-full bg-transparent border-0 px-0 text-lg font-medium leading-snug placeholder:font-normal placeholder:opacity-40 focus:outline-none"
            placeholder="Title">
          ${formatToolbar()}
          <div data-role="composer-body" contenteditable="true" data-placeholder="Take a note…"
            class="g-note-editor bg-transparent text-sm w-full min-h-24 max-h-72 overflow-auto px-0 py-2 leading-snug focus:outline-none"></div>
          <div class="flex items-center gap-1">
            <div class="flex flex-wrap gap-1">${swatches}</div>
            <div class="ml-auto flex gap-1">
              <button data-role="composer-add" class="btn btn-primary btn-xs">Add</button>
              <button data-role="composer-close" class="btn btn-ghost btn-xs">Close</button>
            </div>
          </div>
        </div>
      </div>`;
    this._composerColor = '';
    this._composerBuilt = true;
  }

  _syncComposerVisibility() { this._ensureComposer(); }

  _expandComposer() {
    const exp = this._composerWrap.querySelector('[data-role="composer-expanded"]');
    if (!exp) return;
    exp.classList.remove('hidden');
    // The trigger is only a stand-in for the closed form; once the real editor
    // is on screen a second "Take a note…" row is just noise.
    const trig = this._composerWrap.querySelector('[data-role="composer-trigger"]');
    if (trig) trig.hidden = true;
    // Focus the title, not the body: the form opens top-down and hiding the
    // trigger drops focus to the document, so something must claim it.
    const title = exp.querySelector('[data-role="composer-title"]');
    if (title) title.focus();
  }

  _collapseComposer() {
    const exp = this._composerWrap.querySelector('[data-role="composer-expanded"]');
    if (exp) exp.classList.add('hidden');
    const trig = this._composerWrap.querySelector('[data-role="composer-trigger"]');
    if (trig) { trig.hidden = false; trig.value = ''; }
    const title = this._composerWrap.querySelector('[data-role="composer-title"]');
    const body = this._composerWrap.querySelector('[data-role="composer-body"]');
    if (title) title.value = '';
    if (body) body.innerHTML = '';
    this._composerColor = '';
    this._applyComposerColor();
  }

  // Paints the composer in the colour the note WILL be, before Add is pressed —
  // the same live preview the edit modal gives. _collapseComposer resets
  // _composerColor and calls this, so closing the composer clears the tint too.
  _applyComposerColor() {
    const box = this._composerWrap.querySelector('[data-role="composer-box"]');
    // '' and 'default' both yield an empty style, i.e. back to the base surface.
    if (box) box.setAttribute('style', noteTintStyle(this._composerColor));
    this._composerWrap.querySelectorAll('[data-role="composer-color"]').forEach(b => {
      const on = b.getAttribute('data-color') === this._composerColor && this._composerColor !== '';
      b.classList.toggle('ring-2', on);
      b.classList.toggle('ring-primary', on);
    });
  }

  // ── Render (grid only; composer is untouched) ──

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      // Never rebuild the DOM out from under an active drag — queue it instead.
      if (this._isDragging) { this._pendingRender = true; return; }
      this._renderGrid();
      this._initSortable();
      // The modal now outlives the tile it came from, so a note deleted or
      // filtered away underneath it would leave Save writing into a void.
      // Skipped while loading: a host toggling a spinner must not nuke an edit.
      if (this._editingId && !this.loading &&
          !this._notes.some((n) => this._noteId(n) === this._editingId)) {
        this._closeEditModal();
      }
    });
  }

  _renderGrid() {
    if (!this._gridWrap) return;
    const cols = (this._config && this._config.cols) || { default: 1, sm: 2, lg: 3 };

    if (this.loading) {
      this._gridWrap.className = buildColumnClasses(cols);
      this._gridWrap.innerHTML = repeat(4, skeletonNote);
      return;
    }
    if (!this._notes.length) {
      this._gridWrap.className = 'block';
      this._gridWrap.innerHTML = `<div class="text-center py-12 text-base-content/50">
        <i class="${escapeHtml(this.emptyIcon)} text-3xl mb-2 block"></i>${escapeHtml(this.emptyText)}</div>`;
      return;
    }

    // Split into pinned / others, preserving the host's order within each group.
    const pinned = [], rest = [];
    for (const n of this._notes) (this._pinned(n) ? pinned : rest).push(n);

    this._noteById = new Map();
    this._gridWrap.className = 'block';
    this._gridWrap.innerHTML = '';

    // When anything is pinned, show labelled "Pinned" / "Others" sections (each
    // its own masonry grid). With nothing pinned, render one ungrouped grid.
    if (pinned.length) {
      this._gridWrap.appendChild(this._renderSection('Pinned', pinned, cols));
      if (rest.length) this._gridWrap.appendChild(this._renderSection('Others', rest, cols, true));
    } else {
      this._gridWrap.appendChild(this._renderSection(null, rest, cols));
    }
  }

  // One section: an optional heading + a masonry grid of its notes. CSS columns
  // can't host a header mid-flow, so each group is a separate grid container.
  _renderSection(label, notes, cols, spaced = false) {
    const section = document.createElement('div');
    if (spaced) section.className = 'mt-6';
    if (label) {
      const h = document.createElement('div');
      h.className = 'text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2 px-1';
      h.textContent = label;
      section.appendChild(h);
    }
    const grid = document.createElement('div');
    grid.className = buildColumnClasses(cols);
    grid.dataset.role = 'notes-grid';
    for (const n of notes) grid.appendChild(this._buildCard(n));
    section.appendChild(grid);
    return section;
  }

  _buildCard(n) {
    const id = this._noteId(n);
    this._noteById.set(id, n);
    const card = document.createElement('g-note-card');
    card.setAttribute('note-id', id);
    card.note = {
      id,
      title: this._title(n),
      color: this._color(n) || 'default',
      pinned: this._pinned(n),
      bodyHtml: renderMarkdown(String(this._body(n) ?? '')),
      bodyRaw: this._body(n) ?? '',
      updatedLabel: relTime(this._updatedAt(n)),
      showColors: id === this._colorPickerFor,
      confirmDelete: id === this._confirmDeleteId,
      palette: this._palette(),
      readonly: this.readonly,
    };
    return card;
  }

  // ── Drag-reorder (SortableJS, global like kanban/task-list) ──
  // One Sortable per section grid, no shared group → a note reorders only
  // within its own pinned/others group; pin state is changed via the pin, not drag.
  _initSortable() {
    this._destroySortable();
    if (this.readonly) return;
    const grids = this._gridWrap ? this._gridWrap.querySelectorAll('[data-role="notes-grid"]') : [];
    if (!grids.length) return;
    if (typeof Sortable === 'undefined') {
      if (!this._warnedNoSortable) {
        console.warn('[g-notes] SortableJS not found — drag-reorder disabled. Include it to enable.');
        this._warnedNoSortable = true;
      }
      return;
    }
    this._sortables = [...grids].map((grid) => new Sortable(grid, {
      animation: 150,
      // No handle and no delay — press the tile and move, and it moves.
      //
      // A delay is the wrong tool here: it makes the card feel unresponsive and
      // it was only there to leave room for text selection. The tile is now
      // `select-none` instead (see g-note-card), which removes the conflict at
      // the source — a press can only ever mean "drag", never "select", so the
      // drag can start on the first pointer movement. Selection stays enabled
      // on the editor and inputs, where it is actually needed.
      //
      // Never begin a drag from a control, a link, or an open editor.
      // preventOnFilter must stay false or the filtered element's own
      // click/caret is swallowed.
      filter: (evt) => {
        const el = evt.target;
        if (!el || !el.closest) return false;
        return !!el.closest('button, a, input, textarea, select, label, [contenteditable="true"]');
      },
      preventOnFilter: false,
      draggable: 'g-note-card',
      ...DRAG_CLASSES,
      onStart: () => { this._isDragging = true; },
      onEnd: (evt) => {
        this._isDragging = false;
        // Stamped so the trailing click a drop generates can be told apart from a
        // real one — see _onClick. NOT stamped in onUnchoose: a press that never
        // moved fires choose/unchoose without start/end, and that click is real.
        this._dragEndedAt = Date.now();
        this._handleDragEnd(evt);
        if (this._pendingRender) { this._pendingRender = false; this.scheduleRender(); }
      },
      onUnchoose: () => { this._isDragging = false; },
    }));
  }

  _destroySortable() {
    if (this._sortables && this._sortables.length) this._sortables.forEach((s) => s.destroy());
    this._sortables = [];
  }

  _handleDragEnd(evt) {
    if (!this._gridWrap || !this._noteById) return;
    // Rebuild the full order from the section grids in DOM order (pinned first).
    const ordered = [];
    this._gridWrap.querySelectorAll('[data-role="notes-grid"]').forEach((grid) => {
      grid.querySelectorAll(':scope > g-note-card').forEach((card) => {
        const note = this._noteById.get(card.getAttribute('note-id'));
        if (note) ordered.push(note);
      });
    });
    // Adopt the on-screen order as our own state, immediately.
    //
    // Without this the component stays out of sync with what the user just did:
    // SortableJS moved the DOM, but `_notes` still holds the old order, and the
    // next render — which can be triggered by something purely internal, like
    // opening a note for edit or showing the colour picker — rebuilds the grid
    // from that stale array and silently undoes the drag.
    //
    // The host cannot be relied on to close this gap: docs/notes.md tells hosts
    // to persist and let the server round-trip re-push `.notes`, so there is a
    // real window where the only correct order lives in the DOM. Keep it here.
    // A later `.notes` assignment from the host still wins, as it should.
    if (ordered.length === this._notes.length) {
      // Remember both sides so a later push that echoes the PRE-drag order can
      // be recognised as stale rather than obeyed.
      this._pendingOrder = {
        before: this._notes.map((n) => this._noteId(n)),
        after: ordered.map((n) => this._noteId(n)),
      };
      this._notes = ordered;
    }

    this._emit('note-moved', {
      noteId: evt.item.getAttribute('note-id'),
      oldIndex: evt.oldIndex,
      newIndex: evt.newIndex,
      notes: ordered,
    });
  }

  // ── Interaction ──

  _onFocusIn = (e) => {
    if (e.target.closest && e.target.closest('[data-role="composer-trigger"]')) this._expandComposer();
  };

  _onClick = (e) => {
    const btn = e.target.closest('[data-role]');
    const role = btn && btn.getAttribute('data-role');

    // Composer
    if (role === 'composer-add') { this._composerAdd(); return; }
    if (role === 'composer-close') { this._collapseComposer(); return; }
    if (role === 'composer-color') { this._composerColor = btn.getAttribute('data-color'); this._applyComposerColor(); return; }

    // Edit modal. Hoisted above the card lookup on purpose: the dialog is a
    // SIBLING of the grid, so these buttons have no owning <g-note-card> and the
    // cardEl-derived branches below would silently no-op on them.
    if (role === 'note-save') { this._saveEdit(); return; }
    if (role === 'note-cancel') { this._closeEditModal(); return; }
    if (role === 'note-modal-swatch') { this._editingColor = btn.getAttribute('data-color'); this._applyModalColor(); return; }

    // Card controls (resolve the owning note)
    const cardEl = e.target.closest('g-note-card');
    const noteId = cardEl && cardEl.getAttribute('note-id');
    const note = noteId && this._noteById ? this._noteById.get(noteId) : null;

    if (role === 'note-pin') { this._emit('note-pinned', { noteId, pinned: !this._pinned(note || {}), note }); return; }
    if (role === 'note-color') { this._colorPickerFor = this._colorPickerFor === noteId ? null : noteId; this._confirmDeleteId = null; this.scheduleRender(); return; }
    if (role === 'note-swatch') { this._colorPickerFor = null; this._emit('note-updated', { noteId, changes: { color: btn.getAttribute('data-color') }, note }); return; }
    if (role === 'note-delete') { this._confirmDeleteId = noteId; this._colorPickerFor = null; this.scheduleRender(); return; }
    if (role === 'note-delete-cancel') { this._confirmDeleteId = null; this.scheduleRender(); return; }
    if (role === 'note-delete-confirm') { this._confirmDeleteId = null; this._emit('note-deleted', { noteId, note }); return; }

    // Whole-tile click. The pen is gone: the tile IS the edit affordance, so the
    // hit area is the card minus its controls, its links, and its chrome rows.
    if (!cardEl || !noteId) return;
    // A drop ends in a click, and _isDragging was already cleared by onEnd.
    if (Date.now() - this._dragEndedAt < DRAG_CLICK_GRACE_MS) return;
    if (e.target.closest(CARD_NO_OPEN)) return;
    // Cancelable so a host with its own detail view calls preventDefault() and
    // keeps the event without getting our modal stacked on top of it.
    const proceed = this._emit('note-clicked', { noteId, note }, true);
    if (proceed && !this.readonly) this._openEditModal(noteId, note);
  };

  // ── Edit modal ──
  //
  // INVARIANT: the dialog's innerHTML is written ONLY here, and cleared only on
  // the way out (_closeEditModal, or the `close` handler for Escape/backdrop).
  // _renderGrid touches _gridWrap and nothing else — that separation is what
  // stops a host's `.notes` re-push from eating the keystrokes you are in the
  // middle of typing.
  _openEditModal(noteId, note) {
    if (!this._dialog || !note || this._dialog.open) return;   // showModal() twice throws
    this._editingId = noteId;
    this._editingNote = note;
    this._editingColor = this._color(note) || 'default';
    this._colorPickerFor = null;
    this._confirmDeleteId = null;
    this._dialog.innerHTML = editModalContent({
      title: this._title(note),
      bodyHtml: renderMarkdown(String(this._body(note) ?? '')),
      color: this._editingColor,
      palette: this._palette(),
    });
    this._dialog.showModal();
    this._modalOpenedAt = Date.now();
    // showModal() would otherwise autofocus the header close button. Focus the
    // body, not the title: clicking an existing note means "keep writing".
    const ed = this._dialog.querySelector('[data-role="note-edit-body"]');
    if (ed) { ed.focus(); placeCaretAtEnd(ed); }
    // Fold away any palette / confirm row left open on a tile. Safe now that the
    // editor is out of the grid — a rebuild can no longer reach it.
    this.scheduleRender();
  }

  // Repaint the box and move the selection ring, without rebuilding the modal —
  // rebuilding would throw away the caret and anything typed so far.
  _applyModalColor() {
    if (!this._dialog) return;
    const box = this._dialog.querySelector('.modal-box');
    if (box) box.setAttribute('style', noteTintStyle(this._editingColor));
    this._dialog.querySelectorAll('[data-role="note-modal-swatch"]').forEach((b) => {
      const on = b.getAttribute('data-color') === this._editingColor;
      b.classList.toggle('ring-2', on);
      b.classList.toggle('ring-primary', on);
    });
  }

  _closeEditModal() {
    // Cleared HERE, synchronously, and not left to the `close` handler: that
    // event is dispatched as a queued task, so _saveEdit — which calls this and
    // then emits note-updated on the very next line — would otherwise hand the
    // host a still-stale _editingId. The handler stays as the catch-all for the
    // dismissals that never reach this method (Escape, backdrop).
    this._editingId = null;
    this._editingNote = null;
    this._editingColor = 'default';
    if (this._dialog && this._dialog.open) this._dialog.close();
    else if (this._dialog) this._dialog.innerHTML = '';
  }

  _composerAdd() {
    const title = (this._composerWrap.querySelector('[data-role="composer-title"]')?.value || '').trim();
    // The composer body is now the same contenteditable the card edit uses, so
    // it serialises back through htmlToMarkdown — `.notes` stays markdown either
    // way and the host never sees HTML.
    const editor = this._composerWrap.querySelector('[data-role="composer-body"]');
    const body = (editor ? htmlToMarkdown(editor.innerHTML) : '').trim();
    if (!title && !body) { this._collapseComposer(); return; }
    const note = { title, body, pinned: false };
    if (this._composerColor) note.color = this._composerColor;
    this._emit('note-created', { note });
    this._collapseComposer();
  }

  // Reads from the dialog, which has no owning card — the old cardEl-scoped
  // version would find nothing and no-op.
  //
  // The order here is load-bearing: read the fields, THEN close, THEN emit.
  // Closing first clears the dialog's innerHTML out from under the reads;
  // emitting first leaves _editingId stale inside the host's own handler.
  _saveEdit() {
    const noteId = this._editingId;
    if (!noteId || !this._dialog) return;
    const title = (this._dialog.querySelector('[data-role="note-edit-title"]')?.value || '').trim();
    // The body is edited as HTML; persist it back as markdown.
    const editor = this._dialog.querySelector('[data-role="note-edit-body"]');
    const body = editor ? htmlToMarkdown(editor.innerHTML) : '';
    // Read before closing: _closeEditModal resets the pending colour.
    const color = this._editingColor;
    // _noteById is only rebuilt on _renderGrid's non-empty path, so the loading
    // and empty early-returns can leave it stale — hence the snapshot fallback.
    const note = (this._noteById && this._noteById.get(noteId)) || this._editingNote || null;
    this._closeEditModal();
    // `color` rides along with the edit rather than committing on click, so one
    // Save carries the whole change and Cancel takes all of it back.
    this._emit('note-updated', { noteId, changes: { title, body, color }, note });
  }

  // ── WYSIWYG toolbar (edit modal + composer) ──
  // Run on mousedown so preventDefault keeps the caret/selection in the editor
  // (a plain click would blur the contenteditable before execCommand fires).
  _onMouseDown = (e) => {
    const btn = e.target.closest && e.target.closest('[data-role="note-fmt"]');
    if (!btn) return;
    e.preventDefault();
    // The same strip drives two editors — the edit modal, and the composer.
    // Scope to whichever contains the button so they can't cross-fire.
    const scope = btn.closest('[data-role="note-edit-modal"], [data-role="composer-expanded"]');
    const editor = scope && scope.querySelector('[data-role="note-edit-body"], [data-role="composer-body"]');
    if (editor) this._exec(btn.getAttribute('data-cmd'), btn.getAttribute('data-arg') || '', editor);
  };

  _exec(cmd, arg, editor) {
    editor.focus();
    try {
      if (cmd === 'formatBlock') {
        const want = arg.toLowerCase();
        let cur = '';
        try { cur = (document.queryCommandValue('formatBlock') || '').toLowerCase(); } catch {}
        document.execCommand('formatBlock', false, cur === want ? '<div>' : `<${want}>`);
      } else {
        document.execCommand(cmd, false, null);
      }
    } catch {}
    this._decorate(editor);
  }

  // execCommand emits bare tags; give new blocks the same classes markdown.js
  // uses so headings/lists look right while editing (serialization ignores classes).
  _decorate(editor) {
    editor.querySelectorAll('ul:not([class])').forEach((el) => (el.className = 'list-disc ml-5 my-1'));
    editor.querySelectorAll('ol:not([class])').forEach((el) => (el.className = 'list-decimal ml-5 my-1'));
    editor.querySelectorAll('h1:not([class])').forEach((el) => (el.className = 'font-semibold text-base'));
    editor.querySelectorAll('h2:not([class])').forEach((el) => (el.className = 'font-semibold text-sm'));
  }

  // Returns false when a cancelable event was preventDefault()ed. Only
  // note-clicked passes cancelable today; every other call site ignores this.
  _emit(name, detail, cancelable = false) {
    return this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true, cancelable }));
  }
}

function buildColumnClasses(cols) {
  const out = ['gap-4'];
  out.push(`columns-${cols.default || 1}`);
  if (cols.sm) out.push(`sm:columns-${cols.sm}`);
  if (cols.md) out.push(`md:columns-${cols.md}`);
  if (cols.lg) out.push(`lg:columns-${cols.lg}`);
  if (cols.xl) out.push(`xl:columns-${cols.xl}`);
  return out.join(' ');
}

function repeat(n, fn) { let o = ''; for (let i = 0; i < n; i++) o += fn(); return o; }

// The edit modal's contents. Written ONLY by _openEditModal, cleared ONLY by the
// dialog's `close` handler — never by _renderGrid.
//
// Chrome-free by design: the title and body are borderless and transparent, so
// the note's colour runs the whole height and the dialog reads as the note rather
// than as a form about it. Both sit at px-0 to share a left edge.
//
// There is no "Edit note" heading — the note's own title IS the heading, sharing
// its row with the close button. The dialog keeps `aria-label="Edit note"` (set
// in connectedCallback), so dropping the visible <h3> costs nothing to a screen
// reader; `flex-1 min-w-0` lets a long title truncate instead of shoving the
// close button off the edge.
//
// Deliberately ONE scroller: the contenteditable is the only child that scrolls,
// so the title and the format strip stay pinned while a long note moves under them.
//
// The editor is a FIXED `h-80`, not `flex-1`. Growing to fit would make the modal
// resize under the cursor on the keystroke that wraps a line — and again on every
// one after that. A fixed box keeps the footer where the user left it.
//
// It still adapts downward: `min-h-0` plus the default `flex-shrink: 1` (the
// header, title block and footer are all `shrink-0`) means the editor is the one
// item that gives when the box hits its `max-h` on a short viewport.
function editModalContent(n) {
  return `
  <div class="modal-box max-h-[calc(100vh-100px)] flex flex-col w-full max-w-3xl" style="${noteTintStyle(n.color)}">
    <div class="flex items-center gap-2 shrink-0">
      <input data-role="note-edit-title"
        class="flex-1 min-w-0 select-text bg-transparent border-0 px-0 text-lg font-medium leading-snug placeholder:font-normal placeholder:opacity-40 focus:outline-none"
        placeholder="Title" value="${escapeHtml(String(n.title ?? ''))}">
      <button type="button" data-role="note-cancel" class="btn btn-ghost btn-sm btn-square shrink-0" aria-label="Close">
        <i class="fa-regular fa-xmark"></i>
      </button>
    </div>
    <div class="shrink-0 mb-2 mt-1">${formatToolbar()}</div>
    <div data-role="note-edit-body" contenteditable="true"
      class="g-note-editor select-text h-80 min-h-0 overflow-y-auto bg-transparent text-sm w-full px-0 py-2 leading-snug focus:outline-none">${n.bodyHtml || ''}</div>
    <div class="modal-action shrink-0 border-t border-base-200 pt-4 mt-4">
      <div class="flex flex-wrap items-center gap-1 mr-auto">${modalSwatches(n)}</div>
      <button type="button" data-role="note-cancel" class="btn btn-ghost">Cancel</button>
      <button type="button" data-role="note-save" class="btn btn-primary">Save</button>
    </div>
  </div>
  <form method="dialog" class="modal-backdrop"><button aria-label="Close">close</button></form>`;
}

// The modal's palette row. Its own data-role, NOT the card's `note-swatch`:
// that one commits a recolour on the spot, which is right on a tile but wrong
// behind a Save/Cancel pair — Cancel has to be able to take the colour back.
function modalSwatches(n) {
  const palette = Array.isArray(n.palette) && n.palette.length ? n.palette : NOTE_PALETTE;
  return palette.map((key) => `
    <button type="button" data-role="note-modal-swatch" data-color="${escapeHtml(key)}" title="${escapeHtml(key)}"
      class="w-5 h-5 rounded-full border border-base-content/20${key === 'default' ? ' bg-base-100' : ''}${key === n.color ? ' ring-2 ring-primary' : ''}"
      style="${noteSwatchStyle(key)}"></button>`).join('');
}

function skeletonNote() {
  return `<div class="break-inside-avoid mb-4 rounded-lg border border-base-300 p-3">
    <div class="skeleton h-4 w-2/3 mb-2"></div>
    <div class="skeleton h-3 w-full mb-1"></div>
    <div class="skeleton h-3 w-4/5"></div>
  </div>`;
}

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60); if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60); if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24); if (days < 7) return `${days}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Put the caret at the end of a contenteditable (or input/textarea) on edit-open.
function placeCaretAtEnd(el) {
  if (typeof el.setSelectionRange === 'function') {
    const v = el.value || '';
    try { el.setSelectionRange(v.length, v.length); } catch {}
    return;
  }
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  if (sel) { sel.removeAllRanges(); sel.addRange(range); }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-notes')) {
  customElements.define('g-notes', GNotes);
}
